import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLedgerPendingStates } from './useLedgerPendingStates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/bancos/1touchpay`;
const options = signal => ({ signal, timeout: 20000, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export const useLedgerPage = ({ status, cvu, tipo, estado, page, search }) => {
  const params = new URLSearchParams({ status, page: String(page), limit: '50' });
  if (cvu) params.set('cvu_id', cvu);
  if (tipo) params.set('tipo', tipo);
  if (estado) params.set('estado', estado);
  if (search) params.set('search', search);
  const key = params.toString();
  const [snapshot, setSnapshot] = useState(null);
  const [errorState, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const currentKey = useRef(key);
  currentKey.current = key;
  const flight = useRef(null);
  const sequence = useRef(0);
  const rowFlights = useRef(new Map());
  const active = useRef(true);

  const refresh = useCallback(async () => {
    flight.current?.abort();
    const abort = new AbortController();
    flight.current = abort;
    const id = ++sequence.current;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/ledger?${key}`, options(abort.signal));
      if (!abort.signal.aborted && active.current && id === sequence.current && key === currentKey.current) setSnapshot({ key, data });
    } catch (e) {
      if (!abort.signal.aborted && active.current && id === sequence.current && key === currentKey.current) setError({ key, message: 'No se pudo actualizar el ledger. Podés reintentar con Refrescar.' });
    } finally {
      if (active.current && id === sequence.current) setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    active.current = true;
    refresh();
    return () => {
      active.current = false;
      ++sequence.current;
      flight.current?.abort();
      rowFlights.current.forEach(abort => abort.abort());
      rowFlights.current.clear();
    };
  }, [refresh]);

  const refreshRow = useCallback(async otpId => {
    rowFlights.current.get(otpId)?.abort();
    const abort = new AbortController();
    rowFlights.current.set(otpId, abort);
    try {
      const suffix = cvu ? `?cvu_id=${encodeURIComponent(cvu)}` : '';
      const { data } = await axios.get(`${API}/ledger/movimiento/${otpId}${suffix}`, options(abort.signal));
      if (abort.signal.aborted || !active.current || currentKey.current !== key) return;
      if (String(data.item?.otp_id) !== String(otpId) || cvu && String(data.item.cvu_id) !== String(cvu)) throw new Error('Otra fila');
      // Una carga de lista iniciada antes de guardar no puede deshacer esta lectura.
      flight.current?.abort();
      ++sequence.current;
      setLoading(false);
      setSnapshot(previous => previous?.key === key ? {
        key, data: { ...previous.data, items: previous.data.items.map(m => String(m.otp_id) === String(otpId) ? data.item : m) },
      } : previous);
      setError(null);
    } catch (e) {
      if (!abort.signal.aborted && active.current && currentKey.current === key) setError({ key, message: 'El cambio se guardó, pero no se pudo refrescar la fila. Actualizá el ledger.' });
    } finally {
      if (rowFlights.current.get(otpId) === abort) rowFlights.current.delete(otpId);
    }
  }, [key, cvu]);

  const data = snapshot?.key === key ? snapshot.data : null;
  const error = errorState?.key === key ? errorState.message : '';
  useLedgerPendingStates(data?.items, key, cvu, setSnapshot, setError);
  return {
    items: data?.items || [], pagination: data?.pagination || { page, pages: 0, total: 0 },
    loading: loading || !data && !error, error, refresh, refreshRow,
  };
};